import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Plus, Search, X } from 'lucide-react';
import {
  AGRICULTURAL_CATEGORY_HIERARCHY,
  getCategoryHierarchyMeta
} from '../../utils/agriculturalCategories';
import {
  getAnimalCategoryHierarchy,
  getAnimalCategoryHierarchyMeta
} from '../../utils/animalCategories';
import FarmMegaMenu from './FarmMegaMenu';

export default function FarmSecondaryNav({
  category,
  setCategory,
  setSearchTerm,
  searchTerm,
  t
}) {
  const containerRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Merge the crop macro categories with the animal-products macro so the
  // mega menu exposes both without duplicating the rendering logic.
  const animalHierarchy = useMemo(() => getAnimalCategoryHierarchy(), []);
  const macroCategories = useMemo(
    () => [...AGRICULTURAL_CATEGORY_HIERARCHY, ...animalHierarchy],
    [animalHierarchy]
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeMacroId, setActiveMacroId] = useState(macroCategories[0]?.id || '');

  const selectedHierarchy = useMemo(() => {
    const cropMeta = getCategoryHierarchyMeta(category);
    if (cropMeta) return cropMeta;
    // Fall back to animal hierarchy metadata when an animal category is active.
    return getAnimalCategoryHierarchyMeta(category) || null;
  }, [category]);
  const selectedMacroId = selectedHierarchy?.macroCategoryId || '';
  const categoriesActive = menuOpen || !category;

  const labelForMacro = useCallback(
    (macro) => t(`secondaryNav.macro.${macro.id}`, { defaultValue: macro.name }),
    [t]
  );

  const tabLabelForMacro = useCallback(
    (macro) =>
      t(`secondaryNav.macroShort.${macro.id}`, {
        defaultValue: t(`secondaryNav.macro.${macro.id}`, { defaultValue: macro.name })
      }),
    [t]
  );

  const labelForGroup = useCallback(
    (group) => t(`secondaryNav.group.${group.id}`, { defaultValue: group.name }),
    [t]
  );

  const labelForCrop = useCallback(
    (crop) => {
      const cropKey = String(crop || '')
        .toLowerCase()
        .replace(/[.'"]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      // Crops use the cropCatalog namespace; animal examples fall back to the
      // farmMarketplace secondaryNav namespace so they never leak into
      // crop-only search.
      const cropValue = t(`cropCatalog:crops.${cropKey}`, { defaultValue: '' });
      if (cropValue) return cropValue;
      return t(`farmMarketplace:secondaryNav.crops.${cropKey}`, { defaultValue: crop });
    },
    [t]
  );

  useEffect(() => {
    const hierarchyMeta = getCategoryHierarchyMeta(category);
    if (hierarchyMeta?.macroCategoryId) {
      setActiveMacroId(hierarchyMeta.macroCategoryId);
      return;
    }
    // Animal categories live in their own hierarchy.
    const animalMeta = getAnimalCategoryHierarchyMeta(category);
    if (animalMeta?.macroCategoryId) {
      setActiveMacroId(animalMeta.macroCategoryId);
    }
  }, [category]);

  useEffect(() => {
    if (searchTerm) {
      setSearchOpen(true);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
  }, []);

  const openMenu = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMenuOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMenuOpen(false);
  }, []);

  const scheduleCloseMenu = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      setMenuOpen(false);
    }, 120);
  }, []);

  const openMacro = useCallback(
    (macroId) => {
      setActiveMacroId(macroId);
      openMenu();
    },
    [openMenu]
  );

  const toggleCategoriesMenu = useCallback(() => {
    if (menuOpen) {
      closeMenu();
      return;
    }

    setCategory('');
    if (typeof setSearchTerm === 'function') {
      setSearchTerm('');
    }
    setActiveMacroId(selectedMacroId || macroCategories[0]?.id || '');
    openMenu();
  }, [menuOpen, closeMenu, selectedMacroId, macroCategories, openMenu, setCategory, setSearchTerm]);

  const handleViewGroup = (group) => {
    setCategory(group.primaryCategoryId);
    if (typeof setSearchTerm === 'function') {
      setSearchTerm('');
    }
    closeMenu();
  };

  const handleViewCrop = (crop, group) => {
    setCategory(group.primaryCategoryId);
    if (typeof setSearchTerm === 'function') {
      setSearchTerm(crop);
    }
    closeMenu();
  };

  return (
    <div className="hidden lg:block border-t border-green-700/75 border-b border-green-800/80 bg-gradient-to-b from-green-900 via-green-900 to-green-950 text-emerald-100 shadow-[inset_0_-1px_0_rgba(16,185,129,0.18)]">
      <div ref={containerRef} className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 relative">
        <div className="h-11 xl:h-12 flex items-center gap-3 xl:gap-4">
          <nav
            className="min-w-0 flex-1 overflow-hidden"
            aria-label={t('secondaryNav.label', { defaultValue: 'Farm secondary navigation' })}
            onMouseLeave={scheduleCloseMenu}
          >
            <ul className="flex items-center gap-1.5 xl:gap-2.5 whitespace-nowrap pr-2 -ml-0.5">
              <li>
                <button
                  type="button"
                  onClick={toggleCategoriesMenu}
                  className={`inline-flex h-8 xl:h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition ${
                    categoriesActive
                      ? 'border-emerald-200 bg-emerald-300 text-green-950'
                      : 'border-emerald-300/55 bg-green-950/40 text-emerald-50 hover:border-emerald-200 hover:bg-green-900/65'
                  }`}
                  aria-expanded={menuOpen}
                  aria-controls="farm-mega-menu"
                  aria-label={t('secondaryNav.categories', { defaultValue: 'All categories' })}
                >
                  <Menu className="w-4 h-4" />
                  <span>{t('secondaryNav.categories', { defaultValue: 'Categories' })}</span>
                </button>
              </li>

              {macroCategories.map((macro) => {
                const macroLabel = tabLabelForMacro(macro);
                const isOpenActive = menuOpen && activeMacroId === macro.id;
                const isSelected = !menuOpen && selectedMacroId === macro.id;

                return (
                  <li key={macro.id}>
                    <button
                      type="button"
                      onMouseEnter={() => openMacro(macro.id)}
                      onFocus={() => openMacro(macro.id)}
                      onClick={() => {
                        if (menuOpen && activeMacroId === macro.id) {
                          closeMenu();
                          return;
                        }
                        openMacro(macro.id);
                      }}
                      className={`h-11 xl:h-12 px-1 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                        isOpenActive || isSelected
                          ? 'text-white border-emerald-300'
                          : 'text-emerald-100/85 border-transparent hover:text-white'
                      }`}
                      aria-expanded={isOpenActive}
                      aria-haspopup="menu"
                      aria-controls="farm-mega-menu"
                    >
                      {macroLabel}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="shrink-0 ml-auto flex items-center justify-end gap-2 xl:gap-3 pl-2 xl:pl-4">
            <Link
              to="/sellnow"
              className="inline-flex h-8 xl:h-9 items-center rounded-full border border-emerald-200 bg-emerald-300 px-3.5 text-sm font-semibold text-green-950 transition hover:bg-emerald-200"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('secondaryNav.sellProduce', { defaultValue: 'Sell now' })}
            </Link>

            {!searchOpen ? (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="inline-flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-full border border-emerald-300/55 bg-green-950/50 text-emerald-50 transition hover:border-emerald-200 hover:bg-green-900/75"
                aria-label={t('secondaryNav.openSearch', { defaultValue: 'Open search' })}
              >
                <Search className="w-4 h-4" />
              </button>
            ) : (
              <div className="relative w-52 xl:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-100/80" />
                <input
                  type="search"
                  value={searchTerm || ''}
                  onChange={(event) => {
                    if (typeof setSearchTerm === 'function') {
                      setSearchTerm(event.target.value);
                    }
                  }}
                  placeholder={t('searchPlaceholder')}
                  className="h-8 xl:h-9 w-full rounded-full border border-emerald-300/55 bg-green-950/45 pl-9 pr-9 text-sm text-white placeholder:text-emerald-100/70 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  aria-label={t('searchPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (typeof setSearchTerm === 'function') {
                      setSearchTerm('');
                    }
                    setSearchOpen(false);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-emerald-100/80 hover:text-white"
                  aria-label={t('secondaryNav.closeSearch', { defaultValue: 'Close search' })}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div id="farm-mega-menu" onMouseLeave={scheduleCloseMenu}>
          <FarmMegaMenu
            open={menuOpen}
            macroCategories={macroCategories}
            activeMacroId={activeMacroId}
            onSelectMacro={setActiveMacroId}
            onViewGroup={handleViewGroup}
            onViewCrop={handleViewCrop}
            onMouseEnter={openMenu}
            onMouseLeave={scheduleCloseMenu}
            t={t}
            activeCategory={category}
            searchTerm={searchTerm}
            labelForMacro={labelForMacro}
            labelForGroup={labelForGroup}
            labelForCrop={labelForCrop}
          />
        </div>
      </div>
    </div>
  );
}
